// client/src/utils/pokerCraftParser.ts
//
// Main parser entry point. Re-exporta tipos y funciones desde los módulos
// en ./pokerStats/* para mantener backwards compatibility de imports.

import {
  HistoryStats,
  ParsedTournament,
  Position,
  StackBucket,
  GameFormat,
  MatchupKey,
  MatchupStats,
  makeMatchupKey,
  heroActsAfterOpener,
  CbetTextureStats,
  makeCbetTextureKey,
  FoldToPushStats,
  classifyShoverStack,
} from './pokerStats/types';

import { YUAN_TO_USD } from './pokerStats/constants';

import {
  parsePokerNumber,
  classifyFlopTexture,
  getHeroPosition,
  getPlayerPosition,
  isHeroIP,
  wasLastRaiseAllIn,
  getActions,
  getFlopVillains,
  getStackBucket,
} from './pokerStats/helpers';

import { emptyStats, addStats } from './pokerStats/emptyStats';

// ─── Re-exports para backwards compatibility ─────────────────────────────────
// Otros componentes hacen: import { ParsedTournament, derivedStats, Position } from './pokerCraftParser'
export type {
  HistoryStats,
  ParsedTournament,
  Position,
  StackBucket,
  GameFormat,
  MatchupKey,
  MatchupStats,
} from './pokerStats/types';

export type { DerivedStats, MatchupDerived, FlopTexture } from './pokerStats/types';

export {
  emptyStats,
  addStats,
} from './pokerStats/emptyStats';

export { derivedStats } from './pokerStats/derived';
export { classifyFlopTexture } from './pokerStats/helpers';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers específicos del parseo de archivos de GGPoker
// ─────────────────────────────────────────────────────────────────────────────

function detectFormat(name: string): GameFormat {
  const n = name.toLowerCase();
  if (n.includes('aof') || n.includes('sit & go satellite') || n.includes('flipngo')) return 'AoF';
  if (n.includes('satellite') || n.includes('step to') || n.includes('mega to') || n.includes('t$ builder'))
    return 'Satellite';
  if (n.includes('bounty')) return 'Bounty';
  if (n.includes('spin')) return 'Spin';
  if (n.includes('sit & go') || n.includes('sng')) return 'SNG';
  return 'MTT';
}

function heroBustedFallback(lastHand: string): boolean {
  if (!lastHand) return false;
  const m = lastHand.match(/Seat \d+: Hero \(([\d,]+) in chips\)/);
  if (m && parseInt(m[1].replace(/,/g, '')) === 0) return true;
  if (lastHand.includes('Hero') && lastHand.includes('and is all-in')) {
    const summary = lastHand.split('*** SUMMARY ***')[1] || '';
    if (!summary.match(/Hero.*won|Hero.*collected/) && !lastHand.match(/Hero collected \d/)) return true;
  }
  return false;
}

function heroWonFromBlock(summary: string): boolean {
  if (!summary) return false;
  return /Hero.*won|Hero.*collected/.test(summary);
}

// ─────────────────────────────────────────────────────────────────────────────
// Street state helpers
// ─────────────────────────────────────────────────────────────────────────────

type StreetState = {
  firstBettor: string | null;
  lastAggressor: string | null;
  checkThrough: boolean;
};

function buildStreetState(actions: Array<[string, string]>): StreetState {
  let firstBettor: string | null = null;
  let lastAggressor: string | null = null;
  let betSeen = false;
  let betting = false;

  for (const [player, action] of actions) {
    if (action === 'bets' || action === 'raises') {
      if (!betSeen) {
        firstBettor = player;
        betSeen = true;
      }
      lastAggressor = player;
      betting = true;
    }
    if (action === 'calls') {
      betting = true;
    }
  }

  const checkThrough = actions.length > 0 && !betting;
  return { firstBettor, lastAggressor, checkThrough };
}

function isCbetOpportunity(pfAggressor: string | null, flopState: StreetState): boolean {
  if (pfAggressor !== 'Hero') return false;
  const villainDonk = !!(flopState.firstBettor && flopState.firstBettor !== 'Hero');
  return !villainDonk;
}

function isDonkOpportunity(heroOOP: boolean, lastAggressorPrevStreet: string | null): boolean {
  if (!heroOOP) return false;
  if (!lastAggressorPrevStreet) return false;
  if (lastAggressorPrevStreet === 'Hero') return false;
  return true;
}

function isProbeOpportunity(heroOOP: boolean, previousStreet: StreetState): boolean {
  if (!heroOOP) return false;
  if (!previousStreet.checkThrough) return false;
  return true;
}

function handleDonk(
  hStats: HistoryStats,
  street: 'Flop' | 'Turn' | 'River',
  streetState: StreetState
): void {
  // Mapeamos a los campos del HistoryStats
  if (street === 'Flop') {
    hStats.donkFlopOpp++;
    if (streetState.firstBettor === 'Hero') hStats.donkFlopCount++;
  } else if (street === 'Turn') {
    hStats.donkTurnOpp++;
    if (streetState.firstBettor === 'Hero') hStats.donkTurnCount++;
  } else {
    hStats.donkRiverOpp++;
    if (streetState.firstBettor === 'Hero') hStats.donkRiverCount++;
  }
}

function handleProbe(
  hStats: HistoryStats,
  street: 'Turn' | 'River',
  streetState: StreetState
): void {
  if (street === 'Turn') {
    hStats.probeTurnOpp++;
    if (streetState.firstBettor === 'Hero') hStats.probeTurnCount++;
  } else {
    hStats.probeRiverOpp++;
    if (streetState.firstBettor === 'Hero') hStats.probeRiverCount++;
  }
}

// ─── Bounty extraction del history ──────────────────────────────────────────
function extractBountiesFromHistory(content: string): number {
  let totalBounties = 0;
  const patterns = [
    /Hero collected \$([\d,.]+) for eliminating/gi,
    /Hero received \$([\d,.]+) bounty/gi,
    /Hero wins \$([\d,.]+) bounty/gi,
  ];
  for (const re of patterns) {
    const matches = content.matchAll(re);
    for (const m of matches) {
      totalBounties += parsePokerNumber(m[1]);
    }
  }
  return totalBounties;
}

// ─── BB Defense tracking ─────────────────────────────────────────────────────
function updateBBDefense(
  hStats: HistoryStats,
  pos: Position,
  pfActions: Array<[string, string]>,
  heroFirstAction: string | null
): void {
  if (pos !== 'BB') return;
  const opener = pfActions.find(([p, a]) => p !== 'Hero' && a === 'raises');
  if (!opener) return;
  hStats.bbDefendOpp = (hStats.bbDefendOpp || 0) + 1;
  if (heroFirstAction === 'calls') hStats.bbDefendCall = (hStats.bbDefendCall || 0) + 1;
  if (heroFirstAction === 'raises') hStats.bbDefendRaise = (hStats.bbDefendRaise || 0) + 1;
  if (heroFirstAction === 'folds') hStats.bbDefendFold = (hStats.bbDefendFold || 0) + 1;
}

// ─── Check-raise flop ───────────────────────────────────────────────────────
// Detecta si hero tuvo la oportunidad de check-raise en el flop y si la ejecutó.
// Oportunidad: hero chequeó y, después de ese check, un villain beteó/raiseó.
// Ejecución: hero raiseó después de que el villain beteara.
function detectFlopCheckRaise(actions: Array<[string, string]>): { opp: boolean; xraised: boolean } {
  let heroChecked = false;
  let villainBetAfterCheck = false;
  let heroXRaised = false;

  for (const [p, a] of actions) {
    if (!villainBetAfterCheck && p === 'Hero' && a === 'checks') {
      heroChecked = true;
    }
    if (heroChecked && !villainBetAfterCheck && p !== 'Hero' && (a === 'bets' || a === 'raises')) {
      villainBetAfterCheck = true;
    }
    if (villainBetAfterCheck && p === 'Hero' && a === 'raises') {
      heroXRaised = true;
    }
  }

  return { opp: heroChecked && villainBetAfterCheck, xraised: heroXRaised };
}

// ─── Cbet dry counters ──────────────────────────────────────────────────────
function updateCbetDryCounters(
  hStats: HistoryStats,
  flopTexture: string,
  firstBetFlopIsHero: boolean,
  heroWon: boolean,
  hasTurn: boolean
): void {
  if (flopTexture === 'dry') {
    hStats.flopDryCount = (hStats.flopDryCount || 0) + 1;
    if (firstBetFlopIsHero) {
      hStats.cbetFlopDryCount = (hStats.cbetFlopDryCount || 0) + 1;
      if (!hasTurn && heroWon) {
        hStats.cbetFlopDrySuccessCount = (hStats.cbetFlopDrySuccessCount || 0) + 1;
      }
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// File type detection
// ─────────────────────────────────────────────────────────────────────────────
export function detectFileType(content: string): 'history' | 'summary' | 'unknown' {
  const t = content.trimStart();
  if (t.startsWith('Poker Hand #')) return 'history';
  if (t.startsWith('Tournament #')) return 'summary';
  return 'unknown';
}

// ─────────────────────────────────────────────────────────────────────────────
// SummaryData (intermedio, solo dentro del parser)
// ─────────────────────────────────────────────────────────────────────────────
interface SummaryData {
  tournamentId: string;
  rawName: string;
  format: GameFormat;
  buyIn: number;
  totalCost: number;
  rake: number;
  bountyComponent: number;
  totalPlayers: number;
  prizePool: number;
  date: string;
  time: string;
  heroFinishPosition: number | null;
  heroPrize: number;
  heroPrizeIsTicket: boolean;
  heroPrizeTicketValue: number;
  heroPrizeTournamentDollars: number;
  heroItm: boolean;
  rebuys: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// HistoryData (intermedio, solo dentro del parser)
// ─────────────────────────────────────────────────────────────────────────────
interface HistoryData extends HistoryStats {
  filename: string;
  tournamentId: string;
  rawName: string;
  date: string;
  time: string;
  avgStackDepthBB: number;
  heroBustedFallback: boolean;
  statsByStack: Record<StackBucket, HistoryStats>;
}

// ─────────────────────────────────────────────────────────────────────────────
// parseSummaryFile
// ─────────────────────────────────────────────────────────────────────────────
export function parseSummaryFile(content: string): SummaryData | null {
  try {
    content = content.replace(/^\uFEFF/, '');
    const line1 = content.match(/^Tournament #(\d+),\s*(.+?),\s*(?:AoF\s+)?Hold'em/m);
    if (!line1) return null;
    const tournamentId = line1[1];
    const rawName = line1[2].trim();

    // Buy-in: soporte $ y ¥
    let base = 0;
    let rake = 0;
    let bounty = 0;
    const buyInUsd = content.match(/Buy-in:\s*\$([\d,.]+)(?:\+\$([\d,.]+))?(?:\+\$([\d,.]+))?/);
    if (buyInUsd) {
      base = parsePokerNumber(buyInUsd[1]);
      rake = parsePokerNumber(buyInUsd[2]);
      bounty = parsePokerNumber(buyInUsd[3]);
    } else {
      const buyInYuan = content.match(/Buy-in:\s*¥([\d,.]+)(?:\+¥([\d,.]+))?(?:\+¥([\d,.]+))?/);
      if (buyInYuan) {
        base = parsePokerNumber(buyInYuan[1]) * YUAN_TO_USD;
        rake = parsePokerNumber(buyInYuan[2]) * YUAN_TO_USD;
        bounty = parsePokerNumber(buyInYuan[3]) * YUAN_TO_USD;
      }
    }

    const prizePoolM = content.match(/^Total Prize Pool:\s*[$¥]?([\d,]+(?:\.\d+)?)/m);
    const prizePool = prizePoolM ? parsePokerNumber(prizePoolM[1]) : 0;

    const isAoF = content.includes('AoF') || rawName.toLowerCase().includes('aof');
    let totalInvestedPerEntry = base + rake + bounty;
    if (isAoF && totalInvestedPerEntry === 0 && prizePool > 0) {
      totalInvestedPerEntry = prizePool / 4;
    }

    const startedM = content.match(/Tournament started (\d{4})\/(\d{2})\/(\d{2}) (\d{2}:\d{2})/);
    const date = startedM ? `${startedM[1]}-${startedM[2]}-${startedM[3]}` : '';
    const time = startedM ? startedM[4] : '';

    const rebuysM = content.match(/You made (\d+) re-entri/);
    const rebuys = rebuysM ? parseInt(rebuysM[1]) : 0;
    const totalCost = totalInvestedPerEntry * (1 + rebuys);

    // Finish & prize — clasificado mutuamente exclusivo
    const finishM = content.match(/^(\d+)(?:st|nd|rd|th)\s*:\s*Hero,\s*(.+)$/m);
    let heroFinishPosition: number | null = null;
    let heroPrize = 0;
    let heroPrizeIsTicket = false;
    let heroPrizeTicketValue = 0;
    let heroPrizeTournamentDollars = 0;

    if (finishM) {
      heroFinishPosition = parseInt(finishM[1]);
      const prizeStr = finishM[2].trim();

      if (prizeStr.startsWith('T$')) {
        const m = prizeStr.match(/T\$([\d,.]+)/);
        heroPrizeTournamentDollars = m ? parsePokerNumber(m[1]) : 0;
      } else if (prizeStr.includes('Entry')) {
        heroPrizeIsTicket = true;
        const m = prizeStr.match(/\$([\d,.]+)/);
        heroPrizeTicketValue = m ? parsePokerNumber(m[1]) : 0;
      } else {
        const totalReceivedM = content.match(/received a total of\s*\$([\d,.]+)/i);
        if (totalReceivedM) {
          heroPrize = parsePokerNumber(totalReceivedM[1]);
        } else {
          const m = prizeStr.match(/\$([\d,.]+)/);
          heroPrize = m ? parsePokerNumber(m[1]) : 0;
        }
      }
    }

    const heroItm = heroPrize > 0 || heroPrizeIsTicket || heroPrizeTournamentDollars > 0;

    const playersM = content.match(/^(\d[\d,]+)\s+Players$/m);
    const totalPlayers = playersM ? parseInt(playersM[1].replace(/,/g, '')) : 0;

    let format: GameFormat = 'MTT';
    if (isAoF) format = 'AoF';
    else if (content.includes('Bounty') || rawName.includes('Bounty')) format = 'Bounty';
    else if (rawName.includes('Satellite')) format = 'Satellite';

    return {
      tournamentId,
      rawName,
      buyIn: totalInvestedPerEntry,
      totalCost,
      rake,
      bountyComponent: bounty,
      totalPlayers,
      prizePool,
      date,
      time,
      heroFinishPosition,
      heroPrize,
      heroPrizeIsTicket,
      heroPrizeTicketValue,
      heroPrizeTournamentDollars,
      heroItm,
      rebuys,
      format,
    };
  } catch (err) {
    const snippet = content.slice(0, 120).replace(/\s+/g, ' ');
    console.error('[parseSummaryFile] Error al parsear summary:', err, { snippet });
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// parseHistoryFile — extrae stats hand by hand
// ─────────────────────────────────────────────────────────────────────────────
export function parseHistoryFile(filename: string, content: string): HistoryData | null {
  try {
    content = content.replace(/^\uFEFF/, '');
    const idMatch = content.match(/Tournament #(\d+)/);
    const nameMatch = content.match(/Tournament #\d+, (.+?) Hold'em/);
    const dateMatch = content.match(/(\d{4}\/\d{2}\/\d{2} \d{2}:\d{2}:\d{2})/);

    if (!idMatch) return null;

    const tournamentId = idMatch[1];
    const rawName = nameMatch ? nameMatch[1].trim() : 'Unknown';
    const dateFull = dateMatch ? dateMatch[1] : '';
    const date = dateFull ? dateFull.split(' ')[0].replace(/\//g, '-') : '';
    const time = dateFull ? dateFull.split(' ')[1].substring(0, 5) : '';

    const handBlocks = content.split(/Poker Hand #TM\d+:/).filter(b => b.trim().length > 100);

    const globalStats = emptyStats();
    const statsByStack: Record<StackBucket, HistoryStats> = {
      short: emptyStats(),
      mid: emptyStats(),
      deep: emptyStats(),
    };

    const stackDepths: number[] = [];

    // Bounties del history completo (no por mano)
    globalStats.bountiesCollected = extractBountiesFromHistory(content);

    for (const block of handBlocks) {
      if (!block.includes('Dealt to Hero [')) continue;

      const hasFlop = block.includes('*** FLOP ***');
      const hasTurn = block.includes('*** TURN ***');
      const hasRiver = block.includes('*** RIVER ***');

      // Stack bucket
      const bbM = block.match(/Level\d+\([\d,]+\/([\d,]+)(?:\([\d,]+\))?/);
      const stM = block.match(/Seat \d+: Hero \(([\d,]+) in chips\)/);

      let currentBucket: StackBucket = 'deep';
      // heroStackBB y heroBB hoisted out para que fases A y C puedan reusar
      let heroStackBB: number | null = null;
      let heroBB: number = 0; // valor del big blind en chips (para calcular stack del shover)
      if (bbM && stM) {
        const bb = parseInt(bbM[1].replace(/,/g, ''));
        const st = parseInt(stM[1].replace(/,/g, ''));
        if (bb > 0) {
          heroBB = bb;
          heroStackBB = st / bb;
          stackDepths.push(heroStackBB);
          currentBucket = getStackBucket(heroStackBB);
        }
      }

      const hStats = emptyStats();
      hStats.handsPlayed = 1;

      const afterHole = block.split('*** HOLE CARDS ***')[1] || '';
      const preflop = hasFlop
        ? afterHole.split('*** FLOP ***')[0]
        : afterHole.split('*** SHOWDOWN ***')[0];
      const summary = block.split('*** SUMMARY ***')[1] || '';

      const pfActions = getActions(preflop);
      const pos = getHeroPosition(block);
      hStats.positionCounts[pos]++;

      let raisesBeforeHero = 0;
      let callersBeforeHero = 0;
      let tableFoldedToHero = true;
      let heroFirstAction: string | null = null;
      let heroCalledPf = false;
      let heroRaisedPf = false;
      let lastPfRaiser: string | null = null;

      for (const [p, a] of pfActions) {
        if (p === 'Hero') {
          if (!heroFirstAction) heroFirstAction = a;
          if (a === 'calls') heroCalledPf = true;
          if (a === 'raises') {
            heroRaisedPf = true;
            lastPfRaiser = 'Hero';
          }
        } else {
          if (a === 'raises') {
            lastPfRaiser = p;
            if (!heroFirstAction) raisesBeforeHero++;
          }
          if (!heroFirstAction && a === 'calls') callersBeforeHero++;
          if (!heroFirstAction && (a === 'calls' || a === 'raises')) {
            tableFoldedToHero = false;
          }
        }
      }

      // Steal attempts
      const firstRaiser = pfActions.find(([, a]) => a === 'raises')?.[0] || null;
      if (firstRaiser && tableFoldedToHero) {
        const raiserPos = getPlayerPosition(block, firstRaiser);

        if (raiserPos === 'BTN') {
          hStats.btnStealOpp = (hStats.btnStealOpp || 0) + 1;
          if (pos === 'SB' || pos === 'BB') {
            if (heroFirstAction === 'raises' || heroFirstAction === 'calls') {
              hStats.btnStealCount = (hStats.btnStealCount || 0) + 1;
            }
          }
        }

        if (raiserPos === 'SB') {
          hStats.sbStealOpp = (hStats.sbStealOpp || 0) + 1;
          if (pos === 'BB') {
            if (heroFirstAction === 'raises' || heroFirstAction === 'calls') {
              hStats.sbStealCount = (hStats.sbStealCount || 0) + 1;
            }
          }
        }
      }

      if (heroCalledPf || heroRaisedPf) hStats.vpipCount++;
      if (heroRaisedPf) hStats.pfrCount++;

      // ─── VPIP por posición ────────────────────────────────────────────────
      // opp = cualquier mano donde hero tiene posición conocida (no OTHER).
      // count = hero metió dinero voluntariamente (call o raise preflop).
      // BB checking sin ningún raise previo NO es VPIP — heroCalledPf y
      // heroRaisedPf ya capturan la semántica correcta de esta distinción.
      if (pos !== 'OTHER') {
        if (!hStats.vpipByPosition) hStats.vpipByPosition = {};
        const vpipBucket = hStats.vpipByPosition[pos] || { opp: 0, count: 0 };
        vpipBucket.opp += 1;
        if (heroCalledPf || heroRaisedPf) vpipBucket.count += 1;
        hStats.vpipByPosition[pos] = vpipBucket;
      }

      // ─── FASE 3: RFI (Raise First In) por posición ───────────────────────
      // Definición estricta: hero tuvo la oportunidad de abrir SOLO si todos
      // los jugadores que actuaron antes que él foldearon. Esto excluye
      // limpers, raises previos, y casos donde hero no actuó.
      if (
        tableFoldedToHero &&
        heroFirstAction !== null &&
        (pos === 'EP' || pos === 'HJ' || pos === 'CO' || pos === 'BTN' || pos === 'SB')
      ) {
        if (!hStats.rfiByPosition) hStats.rfiByPosition = {};
        const bucket = hStats.rfiByPosition[pos] || { opp: 0, count: 0, limps: 0 };
        bucket.opp += 1;
        if (heroFirstAction === 'raises') bucket.count += 1;
        if (heroFirstAction === 'calls') bucket.limps = (bucket.limps || 0) + 1;
        hStats.rfiByPosition[pos] = bucket;

        // ─── FASE A: Push/Fold short stack tracking ────────────────────────
        // Cuando el stack del hero al inicio del preflop está en push/fold
        // territory (<= 15bb), trackear cuál fue su primera acción para
        // medir adherencia a la teoría push-or-fold.
        if (heroStackBB !== null && heroStackBB <= 15) {
          if (!hStats.pushFoldByPosition) hStats.pushFoldByPosition = {};
          const pfBucket = hStats.pushFoldByPosition[pos] || {
            opp: 0,
            pushes: 0,
            limps: 0,
            minRaises: 0,
            folds: 0,
          };
          pfBucket.opp += 1;
          if (heroFirstAction === 'raises') {
            if (wasLastRaiseAllIn(preflop, 'Hero')) {
              pfBucket.pushes += 1;
            } else {
              pfBucket.minRaises += 1;
            }
          } else if (heroFirstAction === 'calls') {
            pfBucket.limps += 1;
          } else if (heroFirstAction === 'folds') {
            pfBucket.folds += 1;
          }
          hStats.pushFoldByPosition[pos] = pfBucket;
        }
      }

      if (raisesBeforeHero === 1) {
        hStats.threebetOpp++;
        if (heroFirstAction === 'raises') hStats.threebetCount++;

        // ─── FASE 4a: 3Bet por matchup posicional (hero como 3bettor) ─────
        // Usamos firstRaiser (el primer raiser del hand) en lugar de
        // lastPfRaiser porque cuando hero 3betea el loop setea
        // lastPfRaiser='Hero', borrando la identidad del opener. firstRaiser
        // captura siempre al jugador que abrió el pot (no a hero).
        //
        // BUG FIX: en mesas short-handed (3-5 jugadores), la lógica de
        // getPlayerPosition puede colapsar seats distintos al mismo label
        // posicional (ej: dos seats se mapean ambos a "EP"), produciendo
        // matchups imposibles como "EP_vs_EP" o "HJ_vs_BTN". Validamos que
        // el matchup sea posicionalmente posible antes de trackear.
        if (firstRaiser && firstRaiser !== 'Hero') {
          const openerPos = getPlayerPosition(block, firstRaiser);
          if (heroActsAfterOpener(pos, openerPos)) {
            const matchupKey = makeMatchupKey(pos, openerPos);
            if (!hStats.threebetByMatchup) hStats.threebetByMatchup = {};
            const bucket: MatchupStats = hStats.threebetByMatchup[matchupKey] || {
              opp: 0,
              count: 0,
              success: 0,
            };
            bucket.opp += 1;
            if (heroFirstAction === 'raises') bucket.count += 1;
            hStats.threebetByMatchup[matchupKey] = bucket;
          }
        }
      }

      // ─── Squeeze preflop ──────────────────────────────────────────────────
      // Hay exactamente 1 raiser + 1+ callers antes que hero → squeeze opp.
      // Solo desde posiciones no-OTHER (misma restricción que 3bet).
      if (raisesBeforeHero === 1 && callersBeforeHero >= 1 && heroFirstAction !== null && pos !== 'OTHER') {
        hStats.squeezeOpp = (hStats.squeezeOpp || 0) + 1;
        if (heroFirstAction === 'raises') hStats.squeezeCount = (hStats.squeezeCount || 0) + 1;
      }

      // ─── 4bet preflop ─────────────────────────────────────────────────────
      // Hay exactamente 2 raises antes de hero (open + 3bet) → hero puede cold 4bet.
      if (raisesBeforeHero === 2 && heroFirstAction !== null) {
        hStats.fourbetOpp = (hStats.fourbetOpp || 0) + 1;
        if (heroFirstAction === 'raises') hStats.fourbetCount = (hStats.fourbetCount || 0) + 1;
      }

      // Detectar pozo 3beteado: 2+ raises preflop (open + 3bet, o más)
      const pfRaiseCount = pfActions.filter(([, a]) => a === 'raises').length;
      const is3BetPot = pfRaiseCount >= 2;

      const heroFoldedPreflop = pfActions.some(([p, a]) => p === 'Hero' && a === 'folds');

      // ─── FASE 2: Split Fold to 3Bet por all-in vs no all-in ──────────────
      // ─── FASE 4b: Fold to 3Bet por matchup (hero como opener vs 3bettor) ─
      //
      // Condiciones estrictas para evitar falsos positivos:
      //   raisesBeforeHero === 0  → hero fue el opener (no el 3bettor)
      //   pfRaiseCount === 2      → exactamente un re-raise posterior (el 3bet)
      //                            excluye cold 4bet, hero 4bet, etc.
      if (
        heroFirstAction === 'raises' &&
        raisesBeforeHero === 0 &&
        pfRaiseCount === 2 &&
        lastPfRaiser !== null &&
        lastPfRaiser !== 'Hero'
      ) {
        hStats.foldTo3betOpp++;
        if (heroFoldedPreflop) hStats.foldTo3betCount++;

        // Matchup breakdown: hero abrió desde su pos, villain (lastPfRaiser)
        // 3beteó desde su pos. La key captura quién enfrentó a quién.
        //
        // BUG FIX (shorthand tables): validar que el 3bettor pudo actuar
        // DESPUÉS que hero, sino es matchup imposible (ruido de mesas
        // shorthand con colapso de posiciones).
        const threebettorPos = getPlayerPosition(block, lastPfRaiser);
        if (heroActsAfterOpener(threebettorPos, pos)) {
          const matchupKey = makeMatchupKey(pos, threebettorPos);
          if (!hStats.foldTo3betByMatchup) hStats.foldTo3betByMatchup = {};
          const bucket: MatchupStats = hStats.foldTo3betByMatchup[matchupKey] || {
            opp: 0,
            count: 0,
            success: 0,
          };
          bucket.opp += 1;
          if (heroFoldedPreflop) bucket.count += 1;
          hStats.foldTo3betByMatchup[matchupKey] = bucket;
        }

        // Detectar si el 3bet fue all-in buscando en el texto preflop
        const isAllIn = wasLastRaiseAllIn(preflop, lastPfRaiser);
        if (isAllIn) {
          hStats.foldTo3betAllinOpp = (hStats.foldTo3betAllinOpp || 0) + 1;
          if (heroFoldedPreflop) {
            hStats.foldTo3betAllinCount = (hStats.foldTo3betAllinCount || 0) + 1;
          }
        } else {
          hStats.foldTo3betNonAllinOpp = (hStats.foldTo3betNonAllinOpp || 0) + 1;
          if (heroFoldedPreflop) {
            hStats.foldTo3betNonAllinCount = (hStats.foldTo3betNonAllinCount || 0) + 1;
          }
        }
      }

      if (['CO', 'BTN', 'SB'].includes(pos) && tableFoldedToHero) {
        hStats.atsOpp++;
        if (heroFirstAction === 'raises') hStats.atsCount++;
      }

      updateBBDefense(hStats, pos, pfActions, heroFirstAction);

      // ─── FASE C: Fold to Push ─────────────────────────────────────────────
      // Detectar si un villain hizo all-in preflop y hero tuvo la decisión de
      // call o fold. Usamos posiciones en el texto (textPos) para determinar
      // orden: hero "enfrenta" un shove si su ÚLTIMA acción preflop ocurre
      // después del shove en el texto secuencial.
      //
      // Cubre los tres casos sin lógica especial:
      //   - Open-shove: villain shovea como primera acción
      //   - 3bet-shove: villain shovea sobre el open de hero
      //   - Squeeze-shove: villain shovea sobre callers + hero
      if (heroBB > 0) {
        // Todos los shoves de villain en el texto preflop (en orden de aparición)
        const shoveRe = /^([A-Za-z0-9_][A-Za-z0-9_-]*): raises [\d,.]+ to ([\d,.]+) and is all-in/gm;
        const villainShoves: { player: string; chips: number; textPos: number }[] = [];
        let sm: RegExpExecArray | null;
        while ((sm = shoveRe.exec(preflop)) !== null) {
          if (sm[1] !== 'Hero') {
            villainShoves.push({ player: sm[1], chips: parsePokerNumber(sm[2]), textPos: sm.index });
          }
        }

        if (villainShoves.length > 0) {
          // Todas las acciones de hero en preflop (en orden)
          const heroActRe = /^Hero: (raises|calls|folds)/gm;
          const heroActs: { action: string; textPos: number }[] = [];
          let hm: RegExpExecArray | null;
          while ((hm = heroActRe.exec(preflop)) !== null) {
            heroActs.push({ action: hm[1], textPos: hm.index });
          }

          if (heroActs.length > 0) {
            const heroLast = heroActs[heroActs.length - 1];
            // El shove relevante: el más reciente antes de la última acción de hero
            const relevantShove = villainShoves
              .filter(s => s.textPos < heroLast.textPos)
              .pop();

            if (relevantShove) {
              const heroFolded = heroLast.action === 'folds';
              const shoverStackBB = relevantShove.chips / heroBB;
              const stackBin = classifyShoverStack(shoverStackBB);

              hStats.foldToPushOpp = (hStats.foldToPushOpp || 0) + 1;
              if (heroFolded) hStats.foldToPushCount = (hStats.foldToPushCount || 0) + 1;

              if (!hStats.foldToPushByShoverStack) hStats.foldToPushByShoverStack = {};
              const binBucket: FoldToPushStats = hStats.foldToPushByShoverStack[stackBin] || { opp: 0, folds: 0 };
              binBucket.opp += 1;
              if (heroFolded) binBucket.folds += 1;
              hStats.foldToPushByShoverStack[stackBin] = binBucket;
            }
          }
        }
      }

      if (hasFlop && !heroFoldedPreflop) {
        hStats.flopsSeen++;

        const heroWon = heroWonFromBlock(summary);
        // "showed" = hero mostró sus cartas; "mucked" = hero llegó al showdown pero no mostró.
        // Ambos cuentan como WTSD (went to showdown). Solo matching "showed" undercounts ~40%.
        const heroReachedShowdown = /Seat \d+: Hero .*(showed|mucked)/.test(summary);

        hStats.wwsfOpp++;
        if (heroWon) hStats.wwsfCount++;
        if (heroReachedShowdown) {
          hStats.wtsdCount++;
          hStats.wsdOpp++;
          if (heroWon) hStats.wsdCount++;
        }

        const flopRaw = block.split('*** FLOP ***')[1] || '';
        const flop = hasTurn ? flopRaw.split('*** TURN ***')[0] : flopRaw.split('*** SUMMARY ***')[0];

        const turnRaw = hasTurn ? block.split('*** TURN ***')[1] || '' : '';
        const turn = hasRiver ? turnRaw.split('*** RIVER ***')[0] : turnRaw.split('*** SUMMARY ***')[0];

        const riverRaw = hasRiver ? block.split('*** RIVER ***')[1] || '' : '';
        const river = riverRaw.split('*** SUMMARY ***')[0];

        const flopActions = getActions(flop);
        const turnActions = getActions(turn);
        const riverActions = getActions(river);

        const flopState = buildStreetState(flopActions);
        const turnState = buildStreetState(turnActions);
        const riverState = buildStreetState(riverActions);

        const flopAggressor = flopState.lastAggressor;
        const turnAggressor = turnState.lastAggressor;

        const heroInFlop = flopActions.some(([p]) => p === 'Hero');
        const heroInTurn = turnActions.some(([p]) => p === 'Hero');
        const heroInRiver = riverActions.some(([p]) => p === 'Hero');

        const heroOOPvsPfRaiser = lastPfRaiser
          ? !isHeroIP(pos, getPlayerPosition(block, lastPfRaiser))
          : false;
        const heroOOPvsFlopAgg = flopAggressor
          ? !isHeroIP(pos, getPlayerPosition(block, flopAggressor))
          : false;
        const heroOOPvsTurnAgg = turnAggressor
          ? !isHeroIP(pos, getPlayerPosition(block, turnAggressor))
          : false;

        const villainActiveFlop = !!lastPfRaiser && flopActions.some(([p]) => p === lastPfRaiser);
        const villainActiveTurn = !!flopAggressor && turnActions.some(([p]) => p === flopAggressor);
        const villainActiveRiver = !!turnAggressor && riverActions.some(([p]) => p === turnAggressor);

        const villainActiveTurnForProbe =
          !!lastPfRaiser && turnActions.some(([p]) => p === lastPfRaiser);

        const heroFoldedFlop = flopActions.some(([p, a]) => p === 'Hero' && a === 'folds');

        const flopSection = (block.split('*** FLOP ***')[1] || '').split('*** TURN ***')[0];
        const flopCardsMatch = flopSection.match(/\[[^\]]+\]/);
        const flopText = flopCardsMatch ? flopCardsMatch[0] : '';
        const firstBetFlopIsHero =
          flopActions.find(([, a]) => a === 'bets' || a === 'raises')?.[0] === 'Hero';
        const texture = classifyFlopTexture(flopText);
        updateCbetDryCounters(hStats, texture, firstBetFlopIsHero, heroWon, hasTurn);

        // ─── FLOP ──────────────────────────────────────────────────────────
        if (heroInFlop && isCbetOpportunity(lastPfRaiser, flopState)) {
          hStats.cbetFlopOpp++;
          if (flopState.firstBettor === 'Hero') {
            hStats.cbetFlopCount++;
            if (!hasTurn && heroWon) hStats.cbetFlopSuccessCount++;
          }

          // ─── FASE 1: matchup breakdown ───────────────────────────────────
          // Hero es el PFR. Identificamos a los villanos que fueron al flop.
          const flopVillains = getFlopVillains(flopActions);
          let matchupKey: MatchupKey | null = null;
          if (flopVillains.length === 1) {
            const vPos = getPlayerPosition(block, flopVillains[0]);
            // Descartar matchups imposibles por colisión de posición en mesas
            // short-handed (mismo label para dos seats distintos, ej EP_vs_EP)
            if (pos !== vPos && pos !== 'OTHER' && vPos !== 'OTHER') {
              matchupKey = makeMatchupKey(pos, vPos);
            }
          } else if (flopVillains.length >= 2) {
            matchupKey = 'multi';
          }

          // Matchup solo en SRP — en 3bet pots los matchups son distintos
          // y se trackean por separado abajo
          if (!is3BetPot && matchupKey !== null) {
            if (!hStats.cbetFlopMatchups) hStats.cbetFlopMatchups = {};
            const bucket: MatchupStats = hStats.cbetFlopMatchups[matchupKey] || {
              opp: 0,
              count: 0,
              success: 0,
            };
            bucket.opp += 1;
            if (flopState.firstBettor === 'Hero') {
              bucket.count += 1;
              if (!hasTurn && heroWon) bucket.success += 1;
            }
            hStats.cbetFlopMatchups[matchupKey] = bucket;
          }

          // ─── FASE B: CBet Flop por textura × IP/OOP (HU only) ────────────
          // Solo trackear si el pot fue HU al flop (1 villain). Multiway tiene
          // dinámicas y benchmarks distintos — se trackea separado abajo.
          if (flopVillains.length === 1) {
            // IP/OOP: hero es IP si está en late position (BTN/CO/HJ/EP),
            // OOP si está en blinds (SB/BB).
            const heroIsIP = pos === 'BTN' || pos === 'CO' || pos === 'HJ' || pos === 'EP';
            const posType: 'ip' | 'oop' = heroIsIP ? 'ip' : 'oop';
            const textureKey = makeCbetTextureKey(texture, posType);

            if (!hStats.cbetFlopByTexture) hStats.cbetFlopByTexture = {};
            const tBucket: CbetTextureStats = hStats.cbetFlopByTexture[textureKey] || {
              opp: 0,
              count: 0,
            };
            tBucket.opp += 1;
            if (flopState.firstBettor === 'Hero') tBucket.count += 1;
            hStats.cbetFlopByTexture[textureKey] = tBucket;
          }

          // ─── FASE B.2: CBet Flop por textura — MULTIWAY (2+ villains) ────
          // Sin IP/OOP — en multiway el concepto es ambiguo (varios villains
          // delante y detrás simultáneamente). Solo segmentamos por textura.
          if (flopVillains.length >= 2) {
            if (!hStats.cbetFlopByTextureMulti) hStats.cbetFlopByTextureMulti = {};
            const mBucket: CbetTextureStats = hStats.cbetFlopByTextureMulti[texture] || {
              opp: 0,
              count: 0,
            };
            mBucket.opp += 1;
            if (flopState.firstBettor === 'Hero') mBucket.count += 1;
            hStats.cbetFlopByTextureMulti[texture] = mBucket;
          }
        }

        // ─── CBet en pozo 3beteado (Hero fue el 3bettor) ────────────────────
        // Oportunidad: Hero PFA en 3bet pot, llega al flop con villano.
        // firstRaiser = el opener original que Hero 3beteó.
        // Split IP/OOP: IP si Hero está en posición vs ese opener.
        if (
          is3BetPot &&
          lastPfRaiser === 'Hero' &&
          heroInFlop &&
          firstRaiser &&
          firstRaiser !== 'Hero'
        ) {
          const openerPos = getPlayerPosition(block, firstRaiser);
          const heroIP = isHeroIP(pos, openerPos);

          hStats.cbet3BetPotOpp = (hStats.cbet3BetPotOpp || 0) + 1;
          if (flopState.firstBettor === 'Hero') {
            hStats.cbet3BetPotCount = (hStats.cbet3BetPotCount || 0) + 1;
          }

          if (heroIP) {
            hStats.cbet3BetPotIPOpp = (hStats.cbet3BetPotIPOpp || 0) + 1;
            if (flopState.firstBettor === 'Hero') {
              hStats.cbet3BetPotIPCount = (hStats.cbet3BetPotIPCount || 0) + 1;
            }
          } else {
            hStats.cbet3BetPotOOPOpp = (hStats.cbet3BetPotOOPOpp || 0) + 1;
            if (flopState.firstBettor === 'Hero') {
              hStats.cbet3BetPotOOPCount = (hStats.cbet3BetPotOOPCount || 0) + 1;
            }
          }
        }

        if (lastPfRaiser && lastPfRaiser !== 'Hero') {
          // heroInFlop: excluye spots donde hero fue all-in preflop (no puede foldear)
          if (heroInFlop && flopState.firstBettor === lastPfRaiser) {
            hStats.foldToCbetFlopOpp++;
            if (heroFoldedFlop) hStats.foldToCbetFlopCount++;
            if (flopActions.some(([p, a]) => p === 'Hero' && a === 'calls')) {
              hStats.callCbetFlopCount++;
            }
          }

          if (heroInFlop && villainActiveFlop && isDonkOpportunity(heroOOPvsPfRaiser, lastPfRaiser)) {
            handleDonk(hStats, 'Flop', flopState);
          }
        }

        // ─── XRaise Flop (check-raise) ────────────────────────────────────
        if (heroInFlop) {
          const xr = detectFlopCheckRaise(flopActions);
          if (xr.opp) {
            hStats.xraiseFlopOpp = (hStats.xraiseFlopOpp || 0) + 1;
            if (xr.xraised) hStats.xraiseFlopCount = (hStats.xraiseFlopCount || 0) + 1;

            if (lastPfRaiser === 'Hero') {
              hStats.xraiseFlopAsPFAOpp = (hStats.xraiseFlopAsPFAOpp || 0) + 1;
              if (xr.xraised) hStats.xraiseFlopAsPFACount = (hStats.xraiseFlopAsPFACount || 0) + 1;
            } else {
              hStats.xraiseFlopNonPFAOpp = (hStats.xraiseFlopNonPFAOpp || 0) + 1;
              if (xr.xraised) hStats.xraiseFlopNonPFACount = (hStats.xraiseFlopNonPFACount || 0) + 1;
            }
          }
        }

        // ─── TURN ──────────────────────────────────────────────────────────
        if (hasTurn) {
          if (lastPfRaiser === 'Hero') {
            if (heroInTurn && flopState.firstBettor === 'Hero') {
              hStats.cbetTurnOpp++;
              if (turnState.firstBettor === 'Hero') {
                hStats.cbetTurnCount++;
                if (!hasRiver && heroWon) hStats.cbetTurnSuccessCount++;
              }
              hStats.doubleBarrelOpp = (hStats.doubleBarrelOpp || 0) + 1;
              if (turnState.firstBettor === 'Hero') {
                hStats.doubleBarrelCount = (hStats.doubleBarrelCount || 0) + 1;
              }
            }

            // Delayed CBet: Hero PFA, flop check-check, Hero primera apuesta turn
            if (heroInFlop && heroInTurn && flopState.checkThrough) {
              hStats.delayCbetOpp = (hStats.delayCbetOpp || 0) + 1;
              if (turnState.firstBettor === 'Hero') {
                hStats.delayCbetCount = (hStats.delayCbetCount || 0) + 1;
              }
            }
          } else if (lastPfRaiser) {
            // "Fold to double barrel": villain apostó flop (cbet) Y apostó turn.
            // Sin verificar el flop, cualquier apuesta del villain en el turn
            // contaría como "double barrel", incluyendo delayed cbets del villain.
            if (
              heroInTurn &&
              flopState.firstBettor === lastPfRaiser &&
              turnState.firstBettor === lastPfRaiser
            ) {
              hStats.foldToCbetTurnOpp++;
              const heroFoldedTurn = turnActions.some(([p, a]) => p === 'Hero' && a === 'folds');
              if (heroFoldedTurn) hStats.foldToCbetTurnCount++;
              if (turnActions.some(([p, a]) => p === 'Hero' && a === 'calls')) {
                hStats.callCbetTurnCount++;
              }
            }

            if (heroInTurn && villainActiveTurn && isDonkOpportunity(heroOOPvsFlopAgg, flopAggressor)) {
              handleDonk(hStats, 'Turn', turnState);
            }

            if (heroInTurn && villainActiveFlop && isProbeOpportunity(heroOOPvsPfRaiser, flopState)) {
              handleProbe(hStats, 'Turn', turnState);
            }
          }
        }

        // ─── RIVER ─────────────────────────────────────────────────────────
        if (hasRiver) {
          if (lastPfRaiser === 'Hero') {
            if (
              heroInRiver &&
              flopState.firstBettor === 'Hero' &&
              turnState.firstBettor === 'Hero'
            ) {
              hStats.cbetRiverOpp++;
              if (riverState.firstBettor === 'Hero') {
                hStats.cbetRiverCount++;
                if (heroWon) hStats.cbetRiverSuccessCount++;
              }
            }
          } else if (lastPfRaiser) {
            // "Fold to triple barrel": villain apostó flop, turn Y river.
            if (
              heroInRiver &&
              flopState.firstBettor === lastPfRaiser &&
              turnState.firstBettor === lastPfRaiser &&
              riverState.firstBettor === lastPfRaiser
            ) {
              hStats.foldToCbetRiverOpp++;
              const heroFoldedRiver = riverActions.some(([p, a]) => p === 'Hero' && a === 'folds');
              if (heroFoldedRiver) hStats.foldToCbetRiverCount++;
              if (riverActions.some(([p, a]) => p === 'Hero' && a === 'calls')) {
                hStats.callCbetRiverCount++;
              }
            }

            if (heroInRiver && villainActiveRiver && isDonkOpportunity(heroOOPvsTurnAgg, turnAggressor)) {
              handleDonk(hStats, 'River', riverState);
            }

            if (heroInRiver && villainActiveTurnForProbe && isProbeOpportunity(heroOOPvsPfRaiser, turnState)) {
              handleProbe(hStats, 'River', riverState);
            }
          }
        }

        // AF / AFq
        const postflopLines = flop + turn + river;
        hStats.afBets += (postflopLines.match(/^Hero: bets/gm) || []).length;
        hStats.afRaises += (postflopLines.match(/^Hero: raises/gm) || []).length;
        hStats.afCalls += (postflopLines.match(/^Hero: calls/gm) || []).length;
        hStats.afChecks += (postflopLines.match(/^Hero: checks/gm) || []).length;
      }

      addStats(globalStats, hStats);
      addStats(statsByStack[currentBucket], hStats);
    }

    const avgStackDepthBB = stackDepths.length
      ? Math.round(stackDepths.reduce((a, b) => a + b, 0) / stackDepths.length)
      : 0;

    return {
      filename,
      tournamentId,
      rawName,
      date,
      time,
      avgStackDepthBB,
      heroBustedFallback: heroBustedFallback(handBlocks[handBlocks.length - 1] || ''),
      statsByStack,
      ...globalStats,
    };
  } catch (err) {
    const snippet = content.slice(0, 120).replace(/\s+/g, ' ');
    console.error('[parseHistoryFile] Error al parsear history:', err, { filename, snippet });
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// buyIn heuristic from name (fallback when no summary)
// ─────────────────────────────────────────────────────────────────────────────
function buyInFromName(rawName: string): { buyIn: number; prizeTicket: number; buyInUnknown: boolean } {
  const fmt = detectFormat(rawName);
  const m = rawName.match(/\$([\d.]+)/);
  const first = m ? parseFloat(m[1]) : 0;
  if (fmt === 'AoF') {
    return { buyIn: Math.round((first / 4) * 100) / 100, prizeTicket: first, buyInUnknown: false };
  }
  if (fmt === 'Satellite') {
    const lead = rawName.match(/^\$([\d.]+)\s/);
    if (lead) return { buyIn: parseFloat(lead[1]), prizeTicket: 0, buyInUnknown: false };
    return { buyIn: 0, prizeTicket: first, buyInUnknown: true };
  }
  return { buyIn: first, prizeTicket: 0, buyInUnknown: first === 0 };
}

// ─────────────────────────────────────────────────────────────────────────────
// merge — combina history + summary en ParsedTournament
// ─────────────────────────────────────────────────────────────────────────────
function merge(history: HistoryData | null, summary: SummaryData | null): ParsedTournament | null {
  if (!history && !summary) return null;

  const rawName = history?.rawName || summary?.rawName || '';
  const format = detectFormat(rawName);
  const name = rawName.replace(/\$[\d.]+/g, '').trim().replace(/\s+/g, ' ');

  let buyIn: number;
  let totalCost: number;
  let rake: number;
  let bountyComponent: number;
  let prizeTicket: number;
  let buyInUnknown: boolean;

  if (summary) {
    buyIn = summary.buyIn;
    totalCost = summary.totalCost;
    rake = summary.rake;
    bountyComponent = summary.bountyComponent;
    prizeTicket = 0;
    buyInUnknown = false;
  } else {
    const fb = buyInFromName(rawName);
    buyIn = fb.buyIn;
    totalCost = fb.buyIn; // sin summary no sabemos rebuys — asumimos 1 entry
    prizeTicket = fb.prizeTicket;
    buyInUnknown = fb.buyInUnknown;
    rake = 0;
    bountyComponent = 0;
  }

  const date = history?.date || summary?.date || '';
  const time = history?.time || summary?.time || '';
  const filename = history?.filename || `summary-${summary!.tournamentId}`;
  const tournamentId = history?.tournamentId || summary!.tournamentId;

  let heroPrize = summary?.heroPrize || 0;
  if (!summary && history && (history.bountiesCollected || 0) > 0) {
    heroPrize = history.bountiesCollected || 0;
  }

  const heroItm = summary ? summary.heroItm : heroPrize > 0;
  const heroBusted = summary ? !summary.heroItm : history?.heroBustedFallback || false;

  const stats = history || emptyStats();

  return {
    filename,
    tournamentId,
    name,
    rawName,
    format,
    summaryLoaded: !!summary,
    historyLoaded: !!history,
    buyIn,
    totalCost,
    rake,
    bountyComponent,
    buyInUnknown,
    prizeTicket,
    totalPlayers: summary?.totalPlayers || 0,
    prizePool: summary?.prizePool || 0,
    heroFinishPosition: summary?.heroFinishPosition || null,
    heroPrize,
    heroPrizeIsTicket: summary?.heroPrizeIsTicket || false,
    heroPrizeTicketValue: summary?.heroPrizeTicketValue || 0,
    heroPrizeTournamentDollars: summary?.heroPrizeTournamentDollars || 0,
    heroItm,
    heroBusted,
    rebuys: summary?.rebuys || 0,
    date,
    time,
    avgStackDepthBB: history?.avgStackDepthBB || 0,
    statsByStack:
      history?.statsByStack || { short: emptyStats(), mid: emptyStats(), deep: emptyStats() },
    ...stats,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// batchParse — procesa muchos archivos juntos
// ─────────────────────────────────────────────────────────────────────────────
export interface BatchParseResult {
  tournaments: ParsedTournament[];
  failedFiles: string[];   // nombres de archivos que fallaron al parsear
}

export function batchParse(files: { name: string; content: string }[]): ParsedTournament[] {
  const summaryMap = new Map<string, SummaryData>();
  const historyMap = new Map<string, HistoryData>();

  for (const { name, content } of files) {
    try {
      const type = detectFileType(content);
      if (type === 'summary') {
        const s = parseSummaryFile(content);
        if (s) {
          summaryMap.set(s.tournamentId, s);
        } else {
          console.warn('[batchParse] Summary no parseado (formato inesperado):', name);
        }
      } else if (type === 'history') {
        const h = parseHistoryFile(name, content);
        if (h) {
          const existing = historyMap.get(h.tournamentId);
          if (existing) {
            // Mergear stats cuando el mismo torneo está en múltiples archivos
            addStats(existing, h);
            for (const bucket of ['short', 'mid', 'deep'] as StackBucket[]) {
              addStats(existing.statsByStack[bucket], h.statsByStack[bucket]);
            }
          } else {
            historyMap.set(h.tournamentId, h);
          }
        } else {
          console.warn('[batchParse] History no parseado (formato inesperado):', name);
        }
      }
    } catch (err) {
      // Aislamos errores por archivo: un fallo no interrumpe el resto del batch
      console.error('[batchParse] Error fatal parseando archivo:', name, err);
    }
  }

  const allIds = new Set(
    Array.from(summaryMap.keys()).concat(Array.from(historyMap.keys()))
  );
  const results: ParsedTournament[] = [];
  for (const id of Array.from(allIds)) {
    const t = merge(historyMap.get(id) || null, summaryMap.get(id) || null);
    if (t) results.push(t);
  }

  results.sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`));
  return results;
}
